import React, {
  useMemo,
  useEffect,
  useLayoutEffect,
  useCallback,
  useState,
  useRef,
} from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Quote.scss";
import Modal from "../../common/components/Modal";
import QuickPeep from "./Peep";
import BrandLoader from "../../common/components/BrandLoader";
import EditStepModal from "../../common/components/EditStepModal";

import { submitQuote } from "../../api/quoteService";
import {
  getPincodeMasterRecord,
  submitFormData,
} from "../../api/formService";
import {
  addDobUpdateMetaIfRequired,
  addOwnershipDobProof,
  syncOwnershipDobInSession,
  resolveLeadDob,
} from "../../common/utils/journeyOwnershipDob";
import { getPremiumResultForDimensions } from "../../api/premiumService";
import { getJourneyData } from "../../api/journeyService";
import { fetchJourneyMastersCached } from "../../api/fetchJourneyMastersCached";
import { logError, logDebug } from "../../common/utils/logger";
import {
  formatCurrency,
  calculateAge,
  formatDateDDMMYYYY,
} from "../../common/utils/formatters";
import {
  PLAN_NAMES_NISCHIT_PENSION,
  TEXT_CONTENT,
  JOURNEY_TYPES,
} from "../../constants";
import { useJourney } from "../../common/hooks/useJourney";
import useQuoteState from "./useQuoteState";
import { journeyActions } from "../../store/journeySlice";
import {
  hydrateStoreFromJourney,
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
} from "../../common/utils/journeyResume";
import NeedhelpModal from "../../common/components/NeedhelpModal";
import GuidedTour from "../../common/components/GuidedTour";
import CustomizePlan from "./CustomisePlanForm";
import JointLifeAnnuitantModal, {
  buildSecondaryAnnuitantRelationOptions,
  resolveSecondaryAnnuitantRelationBizCode,
} from "./JointLifeAnnuitantModal";
import {
  resolveNischitProductCode,
  getNischitProductConfig,
  getNischitVisiblePlanOptions,
  isNischitSecondaryAnnuitantComplete,
  resolveNischitProductAndCoverageCodes,
} from "./productConfig";
import {
  getLeadNationality,
  getLeadStateForBi,
  isAnnuitantSelfRelationshipValue,
} from "../../common/utils/journeyLeadFields";
import {
  pickSecondaryAnnuitantFields,
  PRIMARY_ANNUITANT_FORM_TO_PERSIST,
  PROPOSER_BASIC_FORM_TO_PERSIST,
  PROPOSER_PERSONAL_FORM_TO_PERSIST,
  remapFields,
} from "../../common/utils/nischitRoleFields";

/** Set true to trace ROP/ECBO in console (dev only). */
const DEBUG_QUOTE_TOGGLES = false;
const debugLog = (...args) => {
  if (DEBUG_QUOTE_TOGGLES && import.meta.env?.DEV) {
    logDebug("[Quote Toggles]", ...args);
  }
};

const _getRelevantPremiumErrorMessage = (premiumError) => {
  if (!premiumError)
    return "Premium calculation could not be completed for the selected options.";
  if (typeof premiumError === "string" && premiumError.trim())
    return premiumError.trim();
  if (
    typeof premiumError?.message === "string" &&
    premiumError.message.trim()
  ) {
    return premiumError.message.trim();
  }
  return "Premium calculation could not be completed for the selected options.";
};

/** sessionStorage flag prefix so the guided tour only plays once per
 * session — scoped per application number (see `quoteTourSessionKey` below)
 * so starting a new journey (new application number) in the same tab shows
 * the tour again instead of it staying dismissed from a prior application. */
const QUOTE_TOUR_SESSION_KEY_PREFIX = "rnlic_nischit_quote_tour_seen";

const getFieldValue = (root, selector) =>
  root.querySelector(`${selector} input, ${selector} select`)?.value;

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

/**
 * Stage 2.0 Lead payload may contain deep nested `finalLeadDetails` / `additional`.
 * We prefer latest fields from outer-most finalLeadDetails and keep first-write wins
 * so deeper stale snapshots don't overwrite current values.
 */
const deriveLeadFieldsFromStage2 = (stage2Payload) => {
  if (!isPlainObject(stage2Payload)) return {};
  const acc = {};
  const seen = new WeakSet();
  const pushIfEmpty = (key, value) => {
    if (
      !key ||
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "")
    )
      return;
    if (acc[key] === undefined || acc[key] === null || acc[key] === "") {
      acc[key] = value;
    }
  };
  const visit = (node, depth = 0) => {
    if (!isPlainObject(node) || depth > 12 || seen.has(node)) return;
    seen.add(node);

    Object.entries(node).forEach(([key, value]) => {
      if (!isPlainObject(value) && !Array.isArray(value)) {
        pushIfEmpty(key, value);
      }
    });

    if (isPlainObject(node.finalLeadDetails))
      visit(node.finalLeadDetails, depth + 1);
    if (isPlainObject(node.additional)) visit(node.additional, depth + 1);
    if (isPlainObject(node.annuitant)) visit(node.annuitant, depth + 1);
    if (isPlainObject(node.primaryAnnuity))
      visit(node.primaryAnnuity, depth + 1);
  };

  // Prefer values explicitly sent in finalLeadDetails first, then other stage-2 fields.
  if (isPlainObject(stage2Payload.finalLeadDetails)) {
    visit(stage2Payload.finalLeadDetails, 0);
  }
  visit(stage2Payload, 0);
  return acc;
};

/** Deep-clone for journey / quote payloads (drops functions, safe for JSON). */
const cloneForJourney = (value) => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const stripNestedLeadPayloadKeys = (raw = {}) => {
  if (!raw || typeof raw !== "object") return {};
  const cleaned = { ...raw };
  delete cleaned.additional;
  delete cleaned.finalLeadDetails;
  delete cleaned.currentStage;
  return cleaned;
};

const normalizeEditedLeadData = (raw = {}) => {
  const next = stripNestedLeadPayloadKeys(raw);

  if (!next.lifeAssuredName && next.fullName) next.lifeAssuredName = next.fullName;
  if (!next.fullName && next.lifeAssuredName) next.fullName = next.lifeAssuredName;

  if (!next.lifeAssuredDob && next.dateOfBirth) next.lifeAssuredDob = next.dateOfBirth;
  if (!next.dateOfBirth && next.lifeAssuredDob) next.dateOfBirth = next.lifeAssuredDob;

  if (!next.lifeAssuredGender && next.gender) next.lifeAssuredGender = next.gender;
  if (!next.gender && next.lifeAssuredGender) next.gender = next.lifeAssuredGender;

  if (!next.proposerName && (next.lifeAssuredName || next.fullName)) {
    next.proposerName = next.lifeAssuredName || next.fullName;
  }
  if (!next.proposerDob && (next.lifeAssuredDob || next.dateOfBirth)) {
    next.proposerDob = next.lifeAssuredDob || next.dateOfBirth;
  }
  if (!next.proposerGender && (next.lifeAssuredGender || next.gender)) {
    next.proposerGender = next.lifeAssuredGender || next.gender;
  }

  if (!next.emailId && next.emailAddress) next.emailId = next.emailAddress;
  if (!next.emailAddress && next.emailId) next.emailAddress = next.emailId;

  if (!next.mobileNumber && next.mobileNo) next.mobileNumber = next.mobileNo;
  if (!next.mobileNo && next.mobileNumber) next.mobileNo = next.mobileNumber;

  if (!next.pinCode && next.residencyPinCode) next.pinCode = next.residencyPinCode;
  if (!next.residencyPinCode && next.pinCode) next.residencyPinCode = next.pinCode;

  return next;
};

const applyLeadEditsToJourneyData = (journeyData, mergedLeadData) => {
  if (!journeyData || typeof journeyData !== "object") return journeyData;
  const next = cloneForJourney(journeyData) || {};
  const stageUpdates = splitLeadEditsByStage(mergedLeadData);
  Object.entries(stageUpdates).forEach(([stageKey, fields]) => {
    if (!fields || Object.keys(fields).length === 0) return;
    const prevStage = next?.stages?.[stageKey];
    next.stages = next.stages || {};
    next.stages[stageKey] = {
      ...(prevStage && typeof prevStage === "object" ? prevStage : {}),
      currentStage: stageKey,
      ...fields,
    };
    const prevCurrent = next?.currentStageData?.[stageKey];
    if (prevCurrent && typeof prevCurrent === "object") {
      next.currentStageData[stageKey] = {
        ...prevCurrent,
        currentStage: stageKey,
        ...fields,
      };
    }
  });
  return next;
};

const firstPresentValue = (source, keys) => {
  if (!source || typeof source !== "object" || !Array.isArray(keys)) return undefined;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) continue;
    const value = source[key];
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return undefined;
};

const pickCanonicalLeadFields = (source, mapping) => {
  const out = {};
  Object.entries(mapping).forEach(([canonical, aliases]) => {
    const value = firstPresentValue(source, aliases);
    if (value !== undefined) {
      out[canonical] = value;
    }
  });
  return out;
};

const splitLeadEditsByStage = (leadData) => {
  const basic = remapFields(
    pickCanonicalLeadFields(leadData, {
      mobileNumber: ["proposerMobileNumber", "mobileNumber", "mobileNo", "mobile"],
      dateOfBirth: ["proposerDob", "dateOfBirth", "lifeAssuredDob", "dob"],
      occupation: ["proposerOccupation", "occupation"],
      annualIncome: ["proposerAnnualIncome", "annualIncome", "annIncome"],
      gender: ["proposerGender", "gender", "lifeAssuredGender"],
      annualIncomeBiCode: ["annualIncomeBiCode"],
      annualIncomeRangeLabel: ["annualIncomeRangeLabel"],
    }),
    PROPOSER_BASIC_FORM_TO_PERSIST,
  );
  const personal = remapFields(
    pickCanonicalLeadFields(leadData, {
      fullName: ["proposerName", "fullName", "lifeAssuredName"],
      emailAddress: ["proposerEmailAddress", "emailAddress", "emailId", "email"],
    }),
    PROPOSER_PERSONAL_FORM_TO_PERSIST,
  );
  const annuitantFields = remapFields(
    pickCanonicalLeadFields(leadData, {
      primaryAnnuitantName: ["primaryAnnuitantName"],
      annuitantRelationship: ["primaryAnnuitantRelationship", "annuitantRelationship"],
      annuitantDateOfBirth: ["primaryAnnuitantDob", "annuitantDateOfBirth"],
      annuitantGender: ["primaryAnnuitantGender", "annuitantGender"],
    }),
    PRIMARY_ANNUITANT_FORM_TO_PERSIST,
  );
  const residency = pickCanonicalLeadFields(leadData, {
    residencyPinCode: ["residencyPinCode", "pinCode"],
  });
  const additional = pickCanonicalLeadFields(leadData, {
    nationality: ["nationality"],
    educationalLevel: ["educationalLevel", "educationLevel", "education"],
    isSinglePay: ["isSinglePay"],
  });
  const updates = {};
  if (Object.keys(basic).length) updates.lead_basic = basic;
  if (Object.keys(personal).length) updates.lead_personal = personal;
  if (Object.keys(annuitantFields).length) {
    updates.lead_primary_annuitant = annuitantFields;
  }
  if (Object.keys(residency).length) updates.lead_residency = residency;
  if (Object.keys(additional).length) updates.lead_additional = additional;
  return updates;
};

/** Suffix after annuity amount; aligns with `annuityFrequency` options in `CustomisePlanForm.jsx`. */
const annuityFrequencyToDisplaySuffix = (raw) => {
  const s = String(raw ?? "yearly")
    .trim()
    .toLowerCase();
  if (s.includes("month")) return "/Monthly";
  if (s.includes("quarter")) return "/Quarterly";
  if (s.includes("half")) return "/Half-Yearly";
  if (s.includes("year")) return "/Yearly";
  return "/Yearly";
};

const annuityFrequencyToPaymentsPerYear = (raw) => {
  const s = String(raw ?? "yearly")
    .trim()
    .toLowerCase();
  if (s.includes("month")) return 12;
  if (s.includes("quarter")) return 4;
  if (s.includes("half")) return 2;
  return 1;
};

const _getInputModeFromFrequency = (freq) => {
  if (!freq) return "1";
  const f = String(freq).toLowerCase();
  if (f.includes("month")) return "12";
  if (f.includes("quarter")) return "4";
  if (f.includes("half")) return "6";
  return "1";
};

const NischitPensionQuote = () => {
  const CUSTOMIZE_PREMIUM_BULK_DEBOUNCE_MS = 500;
  /** Collapse rapid dependency updates (Redux / customize debounce) into one plan-option bulk call. */
  const PLAN_OPTIONS_PREFETCH_DEBOUNCE_MS = 100;
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const {
    effectiveLeadResult: leadResult,
    premiumResult,
    premiumLoading,
    premiumError,
    handleBackToLead: _onBack,
    handleQuoteComplete: onComplete,
    applicationNumber,
    caseId,
    multiDimensionPremiumResult,

    handlePremiumFrequencyChange: _onPremiumFrequencyChange,
    handlePlanChange: onPlanChange,

    handleQuoteDimensionsChange: onQuoteDimensionsChange,
    calculateNischitPremiumsBulkForPlanOptions,
  } = useJourney();

  /** Stable ref so bulk-prefetch effect is not retriggered on every journey re-render (avoids cancel + duplicate network). */
  const calculateNischitPremiumsBulkRef = useRef(
    calculateNischitPremiumsBulkForPlanOptions,
  );
  calculateNischitPremiumsBulkRef.current =
    calculateNischitPremiumsBulkForPlanOptions;

  const hasUserEditedQuoteRef = useRef(false);
  const markUserEditedQuote = useCallback(() => {
    hasUserEditedQuoteRef.current = true;
  }, []);
  /** True for one render cycle after handlePlanSelect, so downstream useEffects skip redundant API calls. Cleared by a trailing useEffect. */
  const planChangeInFlightRef = useRef(false);
  const [hasCustomizeFormError, setHasCustomizeFormError] = useState(false);
  const [isFormDirtyAfterCalculate, setIsFormDirtyAfterCalculate] = useState(false);
  // While the guided tour is running, hide CustomisePlanForm's own Calculate
  // button — the tour's "Customize your Plan" step drives it instead (same
  // click/submit, so the same premium API gets called either way).
  
  const [selectedNishchitPlan, setSelectedNishchitPlan] = useState(null);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [showBiFailureModal, setShowBiFailureModal] = useState(false);
  const [biFailureMessage, setBiFailureMessage] = useState("");
  const [showPlanCalculationFailureModal, setShowPlanCalculationFailureModal] =
    useState(false);
  const [planCalculationFailureMessage, setPlanCalculationFailureMessage] =
    useState("");
  // Scoped per application number so running a second/new journey in the
  // same tab (a fresh applicationNumber) shows the guided tour again,
  // instead of the sessionStorage flag from a prior application keeping it
  // dismissed.
  const quoteTourSessionKey = applicationNumber
    ? `${QUOTE_TOUR_SESSION_KEY_PREFIX}_${applicationNumber}`
    : QUOTE_TOUR_SESSION_KEY_PREFIX;
  // GuidedTour reports its real `isRunning` state asynchronously (via
  // onActiveChange, after it mounts) — starting this at `false` meant the
  // very first render's effects (in particular the plan-options bulk
  // prefetch below) always saw `tourActive === false` on initial mount,
  // even when a not-yet-dismissed tour was about to start, letting
  // calculateNishchitBulk fire once before the tour's own gating kicked in.
  // Seed the same "already seen this session?" check GuidedTour itself uses
  // so an undismissed tour is assumed active from the very first render.
  const [tourActive, setTourActive] = useState(() => {
    try {
      return sessionStorage.getItem(quoteTourSessionKey) !== "1";
    } catch {
      return true;
    }
  });
  const [editBulkRefreshTick, setEditBulkRefreshTick] = useState(0);
  const { leadFormData } = useSelector((s) => s.journey);
  const quoteResultFromStore = useSelector((s) => s.journey.quoteResult);
  const journeyData = useSelector((s) => s.journey?.journeyData);
  const journeyStages = useSelector((s) => s.journey?.journeyData?.stages);
  const quoteStageDataFromJourney = useSelector(
    (s) =>
      s.journey?.journeyData?.currentStageData?.["Stage 3.0 Quote"] ??
      s.journey?.journeyData?.stages?.["Stage 3.0 Quote"],
  );
  const leadStage2DataFromJourney = useSelector(
    (s) =>
      s.journey?.journeyData?.currentStageData?.lead_primary_annuitant ??
      s.journey?.journeyData?.stages?.lead_primary_annuitant ??
      s.journey?.journeyData?.currentStageData?.["Stage 2.0 Lead"] ??
      s.journey?.journeyData?.stages?.["Stage 2.0 Lead"] ??
      null,
  );
  const quotePremiumFrequencyRedux = useSelector(
    (s) => s.journey.quotePremiumFrequency,
  );
  const customizePlanData = useSelector(
    (s) => s.journey.customizePlanData || {},
  );
  const apiCache = useSelector((s) => s.journey.apiCache || {});
  const relationshipRows = apiCache?.relationshipdetails?.data;
  const secondaryRelationOptions = useMemo(
    () => buildSecondaryAnnuitantRelationOptions(relationshipRows),
    [relationshipRows],
  );

  useEffect(() => {
    dispatch(fetchJourneyMastersCached("relationshipdetails"));
  }, [dispatch]);

  const [showSecondaryAnnuitantModal, setShowSecondaryAnnuitantModal] =
    useState(false);
  const [secondaryAnnuitantForm, setSecondaryAnnuitantForm] = useState({
    secondaryAnnuitantName: customizePlanData?.secondaryAnnuitantName,
    secondaryAnnuitantDob: customizePlanData?.secondaryAnnuitantDob,
    secondaryAnnuitantGender: customizePlanData?.secondaryAnnuitantGender,
    secondaryAnnuitantRelation: customizePlanData?.secondaryAnnuitantRelation,
  });
  const [, setHasSecondaryAnnuitant] = useState(
    Boolean(
      customizePlanData?.secondaryAnnuitantDob ||
      customizePlanData?.secondaryAnnuitantGender ||
      customizePlanData?.secondaryAnnuitantRelation,
    ),
  );
  const isSecondaryAnnuitantComplete = isNischitSecondaryAnnuitantComplete(
    customizePlanData || {},
  );
  const [debouncedCustomizePremiumAmount, setDebouncedCustomizePremiumAmount] =
    useState(
      customizePlanData?.premiumAmount ?? customizePlanData?.premium ?? null,
    );
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomizePremiumAmount(
        customizePlanData?.premiumAmount ?? customizePlanData?.premium ?? null,
      );
    }, CUSTOMIZE_PREMIUM_BULK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [customizePlanData?.premiumAmount, customizePlanData?.premium]);
  /** Fields from Customize your Plan that flow into the Nishchit bulk / premium payload. */
  const customizePremiumBulkDriver = useMemo(() => {
    const c = customizePlanData || {};
    return {
      premium: debouncedCustomizePremiumAmount,
      ppt: c.premiumPayingTerm ?? null,
      dp: c.withdrawalAfter ?? null,
      annuityFrequency: c.annuityFrequency ?? null,
      premiumPayingFrequency: c.premiumFrequency ?? null,
    };
  }, [
    debouncedCustomizePremiumAmount,
    customizePlanData?.premiumPayingTerm,
    customizePlanData?.withdrawalAfter,
    customizePlanData?.annuityFrequency,
    customizePlanData?.premiumFrequency,
  ]);
  const annuityDisplaySuffix = useMemo(
    () => annuityFrequencyToDisplaySuffix(customizePlanData?.annuityFrequency),
    [customizePlanData?.annuityFrequency],
  );
  const hasMountedCustomiseSyncRef = useRef(false);
  const [planPremiumByOption, setPlanPremiumByOption] = useState({});
  const [planPremiumLoading, setPlanPremiumLoading] = useState(false);
  const lastPlanPrefetchKeyRef = useRef(null);
  // Always-current selected plan-option id, read inside the debounced prefetch callback below
  // so a stale effect closure never misjudges which row's failure should surface to the user.
  const selectedPlanOptionIdRef = useRef(null);

  // Drop-off resume: hydrate secondary annuitant from Stage 3.0 Quote first,
  // then legacy nested Stage 2 / lead_primary_annuitant.
  const hasHydratedSecondaryFromStage2Ref = useRef(false);
  useEffect(() => {
    if (hasHydratedSecondaryFromStage2Ref.current) return;
    const hasLatestSecondaryInCustomize = Boolean(
      customizePlanData?.secondaryAnnuitantName ||
        customizePlanData?.secondaryAnnuitantDob ||
        customizePlanData?.secondaryAnnuitantGender ||
        customizePlanData?.secondaryAnnuitantRelation,
    );
    if (hasLatestSecondaryInCustomize) {
      hasHydratedSecondaryFromStage2Ref.current = true;
      return;
    }
    const fromQuote = pickSecondaryAnnuitantFields(quoteStageDataFromJourney);
    const fromLead = pickSecondaryAnnuitantFields(leadStage2DataFromJourney);
    const incoming = Object.keys(fromQuote).length ? fromQuote : fromLead;
    if (!Object.keys(incoming).length) return;

    hasHydratedSecondaryFromStage2Ref.current = true;
    dispatch(
      journeyActions.setCustomizePlanData({
        ...(customizePlanData || {}),
        ...incoming,
        secondaryAnnuitantDetailsSaved: true,
      }),
    );
  }, [quoteStageDataFromJourney, leadStage2DataFromJourney, dispatch, customizePlanData]);

  const {
    selectedPlan,
    setSelectedPlan,
    showHelpModal,
    setShowHelpModal,
    premiumFrequency,
    setPremiumFrequency,
    submitting,
    setSubmitting,
    submitError: _submitError,
    setSubmitError,
    showSuccessModal,
    setShowSuccessModal,
  } = useQuoteState({});

  const syncAndRedirectIfJourneyAdvanced = useCallback(
    async (effectiveAppNumber, dateOfBirth) => {
      if (!effectiveAppNumber) return false;
      // User returned from summary to edit quote — do not bounce to /summary;
      // allow submitQuote so backend updates case/quote/journey stages.
      if (store.getState().journey?.allowQuoteResubmit) {
        dispatch(journeyActions.setAllowQuoteResubmit(false));
        return false;
      }
      try {
        const latestJourney = await getJourneyData(
          effectiveAppNumber,
          dateOfBirth || null,
        );
        if (!latestJourney || typeof latestJourney !== "object") return false;

        hydrateStoreFromJourney(
          dispatch,
          latestJourney,
          effectiveAppNumber,
          store.getState,
        );
        const resumeRoute = getResumeRouteFromJourney(latestJourney);
        if (!resumeRoute || resumeRoute === "/quote") return false;

        const resumeState =
          resumeRoute === "/proposal"
            ? getResumeStateFromJourney(latestJourney)
            : null;
        navigate(resumeRoute, resumeState ? { state: resumeState } : {});
        return true;
      } catch {
        return false;
      }
    },
    [dispatch, navigate, store],
  );

  const leadInput = leadResult?.leadInput || {};
  const assignedProductName = leadResult?.assignedProductName || "";
  const isEliteEligible = assignedProductName.toLowerCase().includes("elite");
  const pincodeTermPlanAvailability = useSelector(
    (s) => s.journey?.pincodeTermPlanAvailability,
  );
  const _pincodeAllowsElite =
    pincodeTermPlanAvailability == null ||
    pincodeTermPlanAvailability.eliteAllowed !== false;
  /** Eligibility rules allow Elite and pincode master allows Super Suraksha Elite at this location */

  const leadData = useSelector((state) => state.journey?.leadFormData);
  const journeyRehydrated = useSelector(
    (state) => state.journey?._persist?.rehydrated,
  );
  const editDetailsFormData = useMemo(() => {
    const stage10 = journeyStages?.["basic"] ?? {};
    const stage11 = journeyStages?.["personal"] ?? {};
    const stage12 = journeyStages?.["residency"] ?? {};
    const stage20 = journeyStages?.["annuitant"] ?? {};
    const stage30 = journeyStages?.["additional"] ?? {};
    const leadInputFromQuote =
      quoteStageDataFromJourney?.leadInput ??
      quoteResultFromStore?.leadInput ??
      {};
    const stage2LeadFields = deriveLeadFieldsFromStage2(
      leadStage2DataFromJourney,
    );

    const merged = {
      ...(stage10 && typeof stage10 === "object" ? stage10 : {}),
      ...(stage11 && typeof stage11 === "object" ? stage11 : {}),
      ...(stage12 && typeof stage12 === "object" ? stage12 : {}),
      ...(stage20 && typeof stage20 === "object" ? stage20 : {}),
      ...(stage30 && typeof stage30 === "object" ? stage30 : {}),
      ...(stage2LeadFields && typeof stage2LeadFields === "object"
        ? stage2LeadFields
        : {}),
      ...(leadFormData && typeof leadFormData === "object" ? leadFormData : {}),
    };

    if (!merged.fullName && leadInputFromQuote?.lifeAssuredName) {
      merged.fullName = leadInputFromQuote.lifeAssuredName;
    }
    if (!merged.dateOfBirth && leadInputFromQuote?.lifeAssuredDob) {
      merged.dateOfBirth = leadInputFromQuote.lifeAssuredDob;
    }
    if (!merged.emailAddress && leadInputFromQuote?.emailId) {
      merged.emailAddress = leadInputFromQuote.emailId;
    }
    if (!merged.residencyPinCode && leadInputFromQuote?.pinCode) {
      merged.residencyPinCode = leadInputFromQuote.pinCode;
    }
    if (!merged.mobileNumber && leadInputFromQuote?.mobileNumber) {
      merged.mobileNumber = leadInputFromQuote.mobileNumber;
    }
    if (!merged.gender && leadInputFromQuote?.lifeAssuredGender) {
      merged.gender = leadInputFromQuote.lifeAssuredGender;
    }
    if (!merged.tobaccoConsumption && leadInputFromQuote?.smokerStatus) {
      merged.tobaccoConsumption =
        String(leadInputFromQuote.smokerStatus).toLowerCase() === "no"
          ? "It's No for me"
          : "Yes, I Do";
    }
    if (!merged.nationality) {
      merged.nationality = "Yes";
    }

    return merged;
  }, [
    journeyStages,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    leadFormData,
    leadStage2DataFromJourney,
  ]);
  const lifeAssuredName =
    leadData?.fullName || leadData?.lifeAssuredName || leadData?.proposerName || "";
  const lifeAssuredDob =
    leadData?.dateOfBirth || leadData?.lifeAssuredDob || leadData?.proposerDob || "";
  const age = useMemo(() => calculateAge(lifeAssuredDob), [lifeAssuredDob]);
  const formattedDob = useMemo(
    () => formatDateDDMMYYYY(lifeAssuredDob),
    [lifeAssuredDob],
  );

  const premiumAmountForApi = useMemo(() => {
    const raw =
      debouncedCustomizePremiumAmount ??
      customizePlanData?.premiumAmount ??
      customizePlanData?.premium;
    if (raw == null || raw === "") return null;
    const n = Number(String(raw).replace(/,/g, ""));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [
    debouncedCustomizePremiumAmount,
    customizePlanData?.premiumAmount,
    customizePlanData?.premium,
  ]);

  const policyTermForPremium = useMemo(() => {
    const fromCustomize = customizePlanData?.withdrawalAfter;
    if (fromCustomize != null && fromCustomize !== "") {
      const n = Number(fromCustomize);
      if (Number.isFinite(n) && n > 0) return n;
    }
    const fromLead = leadResult?.policyTermOptions;
    if (Array.isArray(fromLead) && fromLead.length) {
      return Math.max(
        ...fromLead.map((v) => Number(v)).filter((n) => Number.isFinite(n)),
      );
    }
    if (fromLead != null && !Array.isArray(fromLead)) {
      const n = Number(fromLead);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 25;
  }, [customizePlanData?.withdrawalAfter, leadResult?.policyTermOptions]);

  const effectiveMultiResult = useMemo(
    () => multiDimensionPremiumResult ?? null,
    [multiDimensionPremiumResult],
  );

  // Prefer dimension-specific result for selected frequency.
  const displayPremiumResult = useMemo(() => {
    if (!effectiveMultiResult) return premiumResult;
    const fromDimensions = getPremiumResultForDimensions(
      effectiveMultiResult,
      premiumFrequency ||
        effectiveMultiResult?.primaryPremiumPaymentMode ||
        "Yearly",
      "RP",
      0,
    );
    return fromDimensions || premiumResult;
  }, [effectiveMultiResult, premiumFrequency, premiumResult]);

  const finalDiscountedPremium =
    displayPremiumResult?.finalDiscountedPremium != null
      ? Number(displayPremiumResult.finalDiscountedPremium)
      : (displayPremiumResult?.totalPayableAmount ??
        displayPremiumResult?.premiumAmount ??
        0);
  const totalPayableAmount =
    displayPremiumResult?.totalPayableAmount != null
      ? Number(displayPremiumResult.totalPayableAmount)
      : finalDiscountedPremium;
  const _finalPremium =
    displayPremiumResult?.finalPremium != null
      ? Number(displayPremiumResult.finalPremium)
      : totalPayableAmount;

  const footerPayAmount = customizePlanData?.premiumAmount;
  const premiumSavingsAmount =
    displayPremiumResult?.premiumSavingsAmount != null
      ? Number(displayPremiumResult.premiumSavingsAmount)
      : 0;
  const onlineDiscount = displayPremiumResult?.onlineDiscount ?? "10%";
  const salariedDiscount = displayPremiumResult?.salariedDiscount ?? "5%";

  const shouldAutoCalculateOnInitialQuoteLoad =
    quoteStageDataFromJourney == null && quoteResultFromStore == null;

  // Restore Quote UI selections from Redux so refresh/new-tab keeps user's choices.
  const hasRestoredQuoteUiFromReduxRef = useRef(false);
  const hasHydratedQuoteCacheFromStageRef = useRef(false);
  useEffect(() => {
    if (!leadResult) return;
    if (journeyRehydrated === false) return;
    if (hasHydratedQuoteCacheFromStageRef.current) return;
    if (
      !quoteStageDataFromJourney ||
      typeof quoteStageDataFromJourney !== "object"
    )
      return;

    hasHydratedQuoteCacheFromStageRef.current = true;

    const stage = quoteStageDataFromJourney;

    if (stage.quoteOptions && typeof stage.quoteOptions === "object") {
      dispatch(journeyActions.setQuoteOptions(stage.quoteOptions));
    }
    if (stage.premiumFrequency || stage.premiumPaymentFrequency) {
      dispatch(
        journeyActions.setQuotePremiumFrequency(
          stage.premiumFrequency ?? stage.premiumPaymentFrequency,
        ),
      );
    }
    // Resume should show saved premium values from store; clear stale API error banner.
    dispatch(journeyActions.setPremiumError(null));
  }, [leadResult, journeyRehydrated, quoteStageDataFromJourney, dispatch]);

  useLayoutEffect(() => {
    if (!leadResult) return;
    if (journeyRehydrated === false) return;
    if (hasRestoredQuoteUiFromReduxRef.current) return;

    const quoteSource =
      quoteStageDataFromJourney && typeof quoteStageDataFromJourney === "object"
        ? quoteStageDataFromJourney
        : quoteResultFromStore;
    const quoteFrequencyFromResult =
      quoteSource?.premiumFrequency ??
      quoteSource?.premiumPaymentFrequency ??
      quoteResultFromStore?.premiumFrequency ??
      quoteResultFromStore?.premiumPaymentFrequency;
    let didRestoreAnyQuoteUi = false;

    const persistedSelectedPlan = customizePlanData?.selectedPlan;

  if (
    persistedSelectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_A ||
    persistedSelectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B
  ) {
    setSelectedPlan(persistedSelectedPlan);
    didRestoreAnyQuoteUi = true;
  }


    // Premium frequency
    const resolvedPremiumFrequency =
      quoteFrequencyFromResult ?? quotePremiumFrequencyRedux;

    if (resolvedPremiumFrequency) {
      setPremiumFrequency(resolvedPremiumFrequency);
      didRestoreAnyQuoteUi = true;
    }

    const noPersistedQuoteUiState =
      quoteStageDataFromJourney == null &&
      quoteResultFromStore == null &&
      quotePremiumFrequencyRedux === "Yearly";

    if (!didRestoreAnyQuoteUi && !noPersistedQuoteUiState) {
      return;
    }

    hasRestoredQuoteUiFromReduxRef.current = true;

  }, [
    leadResult,
    journeyRehydrated,
    quotePremiumFrequencyRedux,
    quoteStageDataFromJourney,
    quoteResultFromStore,
    setPremiumFrequency,
    setSelectedPlan
  ]);

  // Persist Premium Frequency + Premium Payment Term selections in Redux
  // so refresh/new-tab restores the same UI.
  useEffect(() => {
    if (!leadResult) return;
    if (!hasRestoredQuoteUiFromReduxRef.current) return;
    dispatch(journeyActions.setQuotePremiumFrequency(premiumFrequency));
  }, [dispatch, premiumFrequency]);

  useEffect(() => {
    debugLog(
      "Quote mounted (effect []); current ROP/ECBO in state will log on next render",
    );
  }, []);

  // Default plan from eligibility + pincode master (only show/select plans allowed at pincode).
  // useEffect(() => {
  //   if (!assignedProductName) return;
  //   if (visibleTermPlans.length === 1) {
  //     setSelectedPlan(visibleTermPlans[0]);
  //     return;
  //   }
  //   if (visibleTermPlans.length === 0) return;

  //   // Always start on Single Life (PLAN_A) so the first bulk prefetch runs for all single sub-plans.
  //   // User switches to Joint / ECBO-driven Elite explicitly.
  //   if (visibleTermPlans.includes(PLAN_NAMES_NISCHIT_PENSION.PLAN_A)) {
  //     setSelectedPlan(PLAN_NAMES_NISCHIT_PENSION.PLAN_A);
  //     return;
  //   }
  //   setSelectedPlan(visibleTermPlans[0]);
  // }, [assignedProductName, visibleTermPlans, setSelectedPlan]);

  const validateQuoteSubmit = useCallback(() => {
    if (!applicationNumber || !caseId)
      return "Application number or Case ID is missing. Please go back and complete the lead form.";
    return null;
  }, [applicationNumber, caseId]);

  const buildBISummaryPayload = useCallback((options = {}) => {
    const biFormData = editDetailsFormData || {};
    const firstNonBlank = (...values) =>
      values.find(
        (v) =>
          v !== undefined &&
          v !== null &&
          !(typeof v === "string" && v.trim() === ""),
      );
    const stateOverride =
      options?.stateOverride != null &&
      String(options.stateOverride).trim() !== ""
        ? String(options.stateOverride).trim()
        : "";
    const annuitantRelationship = firstNonBlank(
      biFormData?.annuitantRelationship,
      leadFormData?.annuitantRelationship,
      leadInput?.annuitantRelationship,
      "",
    );
    const isSelfAnnuitant = isAnnuitantSelfRelationshipValue(
      annuitantRelationship,
    );

    // Proposer identity — never prefer annuitant* fields here.
    const resolvedProposerName =
      firstNonBlank(
        biFormData?.lifeAssuredName,
        biFormData?.fullName,
        biFormData?.proposerName,
        leadFormData?.lifeAssuredName,
        leadFormData?.fullName,
        leadFormData?.proposerName,
        leadInput?.lifeAssuredName,
        leadInput?.fullName,
        leadInput?.proposerName,
        "",
      ) ?? "";
    const resolvedProposerDob =
      firstNonBlank(
        biFormData?.proposerDob,
        biFormData?.lifeAssuredDob,
        biFormData?.dateOfBirth,
        biFormData?.dob,
        leadFormData?.proposerDob,
        leadFormData?.lifeAssuredDob,
        leadFormData?.dateOfBirth,
        leadFormData?.dob,
        leadInput?.proposerDob,
        leadInput?.lifeAssuredDob,
        leadInput?.dateOfBirth,
        leadInput?.dob,
        "",
      ) ?? "";
    const resolvedProposerGender =
      firstNonBlank(
        biFormData?.proposerGender,
        biFormData?.lifeAssuredGender,
        biFormData?.gender,
        leadFormData?.proposerGender,
        leadFormData?.lifeAssuredGender,
        leadFormData?.gender,
        leadInput?.proposerGender,
        leadInput?.lifeAssuredGender,
        leadInput?.gender,
        "",
      ) ?? "";

    // Primary annuitant — self mirrors proposer; non-self uses annuitant* lead fields.
    const resolvedPrimaryDob = isSelfAnnuitant
      ? resolvedProposerDob
      : (firstNonBlank(
          biFormData?.annuitantDateOfBirth,
          biFormData?.primaryAnnuitantDob,
          leadFormData?.annuitantDateOfBirth,
          leadFormData?.primaryAnnuitantDob,
          leadInput?.annuitantDateOfBirth,
          leadInput?.primaryAnnuitantDob,
          resolvedProposerDob,
          "",
        ) ?? "");
    const resolvedPrimaryGender = isSelfAnnuitant
      ? resolvedProposerGender
      : (firstNonBlank(
          biFormData?.annuitantGender,
          biFormData?.primaryAnnuitantGender,
          leadFormData?.annuitantGender,
          leadFormData?.primaryAnnuitantGender,
          leadInput?.annuitantGender,
          leadInput?.primaryAnnuitantGender,
          resolvedProposerGender,
          "",
        ) ?? "");
    const resolvedPrimaryName = isSelfAnnuitant
      ? resolvedProposerName
      : (firstNonBlank(
          biFormData?.primaryAnnuitantName,
          biFormData?.annuitantFullName,
          leadFormData?.primaryAnnuitantName,
          leadFormData?.annuitantFullName,
          leadInput?.primaryAnnuitantName,
          resolvedProposerName,
          "",
        ) ?? "");

    const resolvedMobile =
      firstNonBlank(
        biFormData?.mobileNumber,
        biFormData?.mobileNo,
        biFormData?.mobile,
        leadFormData?.mobileNumber,
        leadFormData?.mobileNo,
        leadInput?.mobileNumber,
        leadInput?.mobileNo,
        "",
      ) ?? "";
    const resolvedEmail =
      firstNonBlank(
        biFormData?.emailAddress,
        biFormData?.emailId,
        biFormData?.email,
        leadFormData?.emailAddress,
        leadFormData?.emailId,
        leadInput?.emailId,
        leadInput?.emailAddress,
        "",
      ) ?? "";
    const resolvedPinCode =
      firstNonBlank(
        biFormData?.residencyPinCode,
        biFormData?.pinCode,
        biFormData?.pincode,
        leadFormData?.residencyPinCode,
        leadFormData?.pinCode,
        leadInput?.pinCode,
        "",
      ) ?? "";
    const resolvedAnnualIncome =
      firstNonBlank(
        biFormData?.annualIncome,
        biFormData?.annIncome,
        leadFormData?.annualIncome,
        leadInput?.annualIncome,
        "",
      ) ?? "";
    const resolvedAnnualIncomeBiCode =
      firstNonBlank(
        biFormData?.annualIncomeBiCode,
        leadFormData?.annualIncomeBiCode,
        leadInput?.annualIncomeBiCode,
        "",
      ) ?? "";
    const resolvedAnnualIncomeRangeLabel =
      firstNonBlank(
        biFormData?.annualIncomeRangeLabel,
        leadFormData?.annualIncomeRangeLabel,
        leadInput?.annualIncomeRangeLabel,
        "",
      ) ?? "";
    const resolvedEducation =
      firstNonBlank(
        biFormData?.educationalLevel,
        biFormData?.education,
        leadFormData?.educationalLevel,
        leadFormData?.education,
        leadInput?.educationalLevel,
        leadInput?.education,
        "",
      ) ?? "";
    const stateVal =
      stateOverride ||
      getLeadStateForBi(biFormData, leadFormData, leadInput);
    const resolvedNationality = getLeadNationality(
      biFormData,
      leadFormData,
      leadInput,
    );
    const primaryAgeVal = resolvedPrimaryDob
      ? calculateAge(resolvedPrimaryDob)
      : null;
    const proposerAgeVal = resolvedProposerDob
      ? calculateAge(resolvedProposerDob)
      : null;
    const currentProductCode = resolveNischitProductCode(leadFormData || {});
    const isSinglePayProduct = String(currentProductCode) === "22074";
    const resolvedProductId = isSinglePayProduct ? "22074" : "22070";
    const resolvedProductName =
      getNischitProductConfig(currentProductCode)?.name || "Nishchit Pension";
    const prSa = "";
    const prPpt = isSinglePayProduct
      ? "1"
      : (firstNonBlank(
          biFormData?.premiumPayingTerm,
          customizePlanData?.premiumPayingTerm,
          leadInput?.premiumPayingTerm,
          "",
        ) ?? "");
    // Use Excel-calculated annualPremium when available so BI API receives the same value as Excel and returns matching ModalPremium
    const derivedAnnualPremium =
      premiumFrequency === "Yearly"
        ? (totalPayableAmount ?? 0)
        : premiumFrequency === "Monthly"
          ? (totalPayableAmount ?? 0) * 12
          : premiumFrequency === "Quarterly"
            ? (totalPayableAmount ?? 0) * 4
            : (totalPayableAmount ?? 0) * 2;
    const annualPremium =
      displayPremiumResult?.annualPremium != null
        ? Number(displayPremiumResult.annualPremium)
        : derivedAnnualPremium;
    const monthlyPremium = annualPremium / 12;
    // Keep BI modal premium aligned with the pay CTA amount shown to user.
    const payButtonAmount = Number(footerPayAmount ?? totalPayableAmount ?? 0);
    const freezeDateObj = new Date();
    const freezeDateStr = `${freezeDateObj.getDate()} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][freezeDateObj.getMonth()]} ${freezeDateObj.getFullYear()}`;
    const resolvedSmoke = firstNonBlank(
      leadInput?.tobaccoConsumption,
      leadInput?.smokingHabit,
      leadInput?.smokerStatus,
      biFormData?.tobaccoConsumption,
      biFormData?.smokingHabit,
      biFormData?.liSmoke,
      "No",
    );
    const resolvedDefermentPeriod = firstNonBlank(
      biFormData?.defermentPeriod,
      biFormData?.deferment,
      customizePlanData?.withdrawalAfter,
      leadInput?.defermentPeriod,
      leadInput?.deferment,
      "",
    );
    const resolvedSelectedSubPlanName =
      firstNonBlank(
        selectedNishchitPlan?.title,
        selectedNishchitPlan?.label,
        resolvedProductName,
      ) ?? "";
    const productCoverageCodes = resolveNischitProductAndCoverageCodes({
      productCode: resolvedProductId,
      planOption:
        selectedNishchitPlan?.id ||
        customizePlanData?.planOption ||
        customizePlanData?.selectedNishchitPlanId,
      selectedPlan: resolvedSelectedSubPlanName,
    });
    const payloadPremiumFrequency = isSinglePayProduct
      ? "singlepay"
      : (firstNonBlank(
          customizePlanData?.premiumFrequency,
          biFormData?.premiumFrequency,
          premiumFrequency,
          "Yearly",
        ) ?? "Yearly");
    const annuityPaymentFrequency =
      firstNonBlank(
        customizePlanData?.annuityFrequency,
        biFormData?.annuityFrequency,
        "Yearly",
      ) ?? "Yearly";
    const secondarySource = {
      ...(customizePlanData || {}),
      ...(secondaryAnnuitantForm || {}),
    };
    return {
      productName: resolvedProductName,
      selectedPlan: resolvedSelectedSubPlanName,
      applicationNumber: applicationNumber ?? "",
      caseId: caseId ?? "",
      ecs: firstNonBlank(
        biFormData?.ecs,
        leadInput?.ecs,
        leadResult?.premiumDefaults?.ecs,
        "1",
      ),
      channel: firstNonBlank(
        biFormData?.channel,
        leadInput?.channel,
        leadResult?.premiumDefaults?.channel,
        "Online",
      ),
      // inputMode: getInputModeFromFrequency(payloadPremiumFrequency),
      premiumFrequency: payloadPremiumFrequency,
      annuityPaymentFrequency: annuityPaymentFrequency,
      policyTerm: "",
      prPpt,
      maxCoverAmount: "",
      prSa,
      age:
        primaryAgeVal != null && Number.isFinite(Number(primaryAgeVal))
          ? String(primaryAgeVal)
          : "",
      name: resolvedPrimaryName,
      lifeAssuredName: resolvedPrimaryName,
      proposerName: resolvedProposerName,
      fullName: resolvedProposerName,
      gender: resolvedPrimaryGender,
      liGender: resolvedPrimaryGender,
      proposerGender: resolvedProposerGender,
      primaryAnnuitantGender: resolvedPrimaryGender,
      state: stateVal,
      companyState:
        firstNonBlank(biFormData?.companyState, leadInput?.companyState, "0") ??
        "0",
      gstin:
        biFormData?.gstin ?? biFormData?.gstinNumber ?? leadInput?.gstin ?? "",
      sameProposer: isSelfAnnuitant ? "True" : "False",
      annuitantRelationship: annuitantRelationship ?? "",
      dob: resolvedPrimaryDob,
      dateOfBirth: resolvedProposerDob,
      proposerDob: resolvedProposerDob,
      lifeAssuredDob: resolvedPrimaryDob,
      primaryAnnuitantDob: resolvedPrimaryDob,
      primaryAnnuitantAge:
        primaryAgeVal != null && Number.isFinite(Number(primaryAgeVal))
          ? String(primaryAgeVal)
          : "",
      proposerAge:
        proposerAgeVal != null && Number.isFinite(Number(proposerAgeVal))
          ? String(proposerAgeVal)
          : "",
      mobile: resolvedMobile,
      mobileNumber: resolvedMobile,
      email: resolvedEmail,
      emailAddress: resolvedEmail,
      kfd: firstNonBlank(biFormData?.kfd, leadInput?.kfd, "") ?? "",
      pincode: resolvedPinCode,
      // pinCode: resolvedPinCode,
      prAnnprem: String(Math.round(annualPremium)),
      // annualPremium: String(Math.round(annualPremium)),
      prMi: String(Math.round(monthlyPremium)),
      prModalPrem: String(Math.round(payButtonAmount)),
      // modalPrem: String(Math.round(payButtonAmount)),
      freezeDate: freezeDateStr,
      // FreezeDate: freezeDateStr,
      businessTypeId: null,
      occupation:
        firstNonBlank(
          biFormData?.occupationClass,
          leadInput?.occupationClass,
          "0",
        ) ?? "0",
      education: resolvedEducation,
      // occupationClass: "",
      isPdf: firstNonBlank(biFormData?.isPdf, leadInput?.isPdf, "1") ?? "1",
      ageProof:
        firstNonBlank(biFormData?.ageProof, leadInput?.ageProof, "1") ?? "1",
      prChannel:
        firstNonBlank(biFormData?.prChannel, leadInput?.prChannel, "7") ?? "7",
      empDiscount:
        firstNonBlank(
          biFormData?.empDiscount,
          leadInput?.empDiscount,
          leadInput?.employeeFlag,
          "0",
        ) ?? "0",
      annIncome: resolvedAnnualIncome,
      // annualIncome: resolvedAnnualIncome,
      annualIncomeBiCode: resolvedAnnualIncomeBiCode,
      annualIncomeRangeLabel: resolvedAnnualIncomeRangeLabel,
      secondaryAnnuitantName:
        firstNonBlank(
          secondarySource?.secondaryAnnuitantName,
          "",
        ) ?? "",
      secondaryAnnuitantDob:
        firstNonBlank(secondarySource?.secondaryAnnuitantDob, "") ?? "",
      secondaryAnnuitantGender:
        firstNonBlank(secondarySource?.secondaryAnnuitantGender, "") ?? "",
      secondaryAnnuitantRelation:
        firstNonBlank(secondarySource?.secondaryAnnuitantRelation, "") ?? "",
      secondaryAnnuitantRelationBizCode:
        resolveSecondaryAnnuitantRelationBizCode(
          relationshipRows,
          secondarySource?.secondaryAnnuitantRelation,
        ) ||
        firstNonBlank(secondarySource?.secondaryAnnuitantRelationBizCode, "") ||
        "",
      fundStrategyId:
        firstNonBlank(
          biFormData?.fundStrategyId,
          leadInput?.fundStrategyId,
          "3",
        ) ?? "3",
      prId: resolvedProductId,
      ...productCoverageCodes,
      // productId: resolvedProductId,
      // productCode: resolvedProductId,
      agentId: "",
      smokingHabit: resolvedSmoke,
      // tobaccoConsumption: resolvedSmoke,
      // liSmoke: resolvedSmoke,
      defermentPeriod:
        resolvedDefermentPeriod != null ? String(resolvedDefermentPeriod) : "",
      nationality: resolvedNationality,
      basedInIndia: resolvedNationality,
    };
  }, [
    leadResult,
    leadInput,
    applicationNumber,
    caseId,
    selectedPlan,
    premiumFrequency,
    leadFormData,
    selectedNishchitPlan?.title,
    selectedNishchitPlan?.label,
    customizePlanData,
    secondaryAnnuitantForm,
    totalPayableAmount,
    footerPayAmount,
    displayPremiumResult,
    editDetailsFormData,
    relationshipRows,
  ]);

  const buildQuotePayload = useCallback(() => {
    const currentProductCode = resolveNischitProductCode(leadFormData || {});
    const isSinglePayProduct = String(currentProductCode) === "22074";
    const resolvedProductId = isSinglePayProduct ? "22074" : "22070";
    const resolvedProductName =
      getNischitProductConfig(currentProductCode)?.name || "Nishchit Pension";
    const payloadPremiumFrequency = isSinglePayProduct
      ? "singlepay"
      : (customizePlanData?.premiumFrequency ||
          editDetailsFormData?.premiumFrequency ||
          premiumFrequency ||
          "Yearly");
    const annuityPaymentFrequency =
      customizePlanData?.annuityFrequency ||
      editDetailsFormData?.annuityFrequency ||
      "Yearly";
    const secondaryFields = pickSecondaryAnnuitantFields({
      ...(customizePlanData || {}),
      ...(secondaryAnnuitantForm || {}),
    });
    const relationBizCode = resolveSecondaryAnnuitantRelationBizCode(
      relationshipRows,
      secondaryFields.secondaryAnnuitantRelation,
    );
    if (relationBizCode) {
      secondaryFields.secondaryAnnuitantRelationBizCode = relationBizCode;
    }
    const productCoverageCodes = resolveNischitProductAndCoverageCodes({
      productCode: resolvedProductId,
      planOption:
        selectedNishchitPlan?.id ||
        customizePlanData?.planOption ||
        customizePlanData?.selectedNishchitPlanId,
      selectedPlan:
        selectedNishchitPlan?.title ||
        selectedNishchitPlan?.label ||
        customizePlanData?.selectedPlan ||
        selectedPlan,
    });
    return {
      productName: resolvedProductName,
      productId: resolvedProductId,
      planOption:
        selectedNishchitPlan?.id ||
        customizePlanData?.planOption ||
        customizePlanData?.selectedNishchitPlanId,
      ...productCoverageCodes,
      selectedPlan:
        customizePlanData?.selectedPlan || selectedPlan || undefined,
      premiumFrequency: payloadPremiumFrequency,
      annuityPaymentFrequency: annuityPaymentFrequency,
      premiumAmount:
        Number(totalPayableAmount) > 0
          ? totalPayableAmount
          : (premiumAmountForApi ?? totalPayableAmount),
      totalPayableAmount:
        Number(totalPayableAmount) > 0
          ? totalPayableAmount
          : (premiumAmountForApi ?? totalPayableAmount),
      policyTerm: policyTermForPremium ?? null,
      onlineDiscount: displayPremiumResult?.onlineDiscount || onlineDiscount,
      salariedDiscount:
        displayPremiumResult?.salariedDiscount || salariedDiscount,
      returnOfPremium: false,
      switchedToPlusDueToROP: false,
      isEliteEligible,
      premiumResult: cloneForJourney(premiumResult),
      eligibilityStatus: leadResult?.eligibilityStatus || "Eligible",
      policyTermOptions: leadResult?.policyTermOptions,
      quoteStage: "Success",
      ...secondaryFields,
    };
  }, [
    applicationNumber,
    caseId,
    leadResult,
    leadFormData,
    leadInput,
    selectedPlan,
    selectedNishchitPlan?.id,
    selectedNishchitPlan?.title,
    selectedNishchitPlan?.label,
    premiumFrequency,
    customizePlanData?.premiumFrequency,
    customizePlanData?.annuityFrequency,
    customizePlanData?.secondaryAnnuitantName,
    customizePlanData?.secondaryAnnuitantDob,
    customizePlanData?.secondaryAnnuitantGender,
    customizePlanData?.secondaryAnnuitantRelation,
    customizePlanData?.secondaryAnnuitantRelationBizCode,
    relationshipRows,
    secondaryAnnuitantForm,
    leadFormData?.annuitantDateOfBirth,
    leadFormData?.primaryAnnuitantDob,
    leadFormData?.proposerDob,
    leadFormData?.proposerGender,
    leadFormData?.annuitantGender,
    leadFormData?.primaryAnnuitantGender,
    editDetailsFormData?.annuitantDateOfBirth,
    editDetailsFormData?.primaryAnnuitantDob,
    editDetailsFormData?.annuitantGender,
    editDetailsFormData?.primaryAnnuitantGender,
    editDetailsFormData?.proposerDob,
    editDetailsFormData?.proposerGender,
    editDetailsFormData?.dob,
    editDetailsFormData?.dateOfBirth,
    editDetailsFormData?.gender,
    editDetailsFormData?.lifeAssuredDob,
    editDetailsFormData?.lifeAssuredGender,
    leadInput?.annuitantDateOfBirth,
    leadInput?.primaryAnnuitantDob,
    leadInput?.proposerDob,
    leadInput?.proposerGender,
    leadInput?.dateOfBirth,
    leadInput?.gender,
    editDetailsFormData?.annuityFrequency,
    totalPayableAmount,
    premiumAmountForApi,
    policyTermForPremium,
    displayPremiumResult,
    premiumSavingsAmount,
    onlineDiscount,
    salariedDiscount,
    isEliteEligible,
    premiumResult,
  ]);

  const handlePlanSelect = useCallback(
    (plan) => {
      markUserEditedQuote();
      planChangeInFlightRef.current = true;
      if (plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B) {
        if (!isSecondaryAnnuitantComplete) {
          setShowSecondaryAnnuitantModal(true);
          return;
        }
        lastPlanPrefetchKeyRef.current = null;
      }
      if (plan === PLAN_NAMES_NISCHIT_PENSION.PLAN_A) {
        setShowSecondaryAnnuitantModal(false);
        lastPlanPrefetchKeyRef.current = null;
      }
      setSelectedPlan(plan);
      dispatch(
  journeyActions.setCustomizePlanData({
    ...(customizePlanData || {}),
    selectedPlan: plan,
  }),
);
      // During the guided tour, picking a plan only records the choice —
      // the premium recalculation is deferred until the tour's own
      // "Calculate" button is clicked (see handleTourCalculatePlanOptions),
      // instead of firing the instant the radio is selected.
      if (tourActive) return;
      onPlanChange?.(
        plan,
        premiumFrequency,
        premiumAmountForApi,
        null,
        null,
        policyTermForPremium,
      );
    },
     [
    setSelectedPlan,
    dispatch,
    customizePlanData,
    onPlanChange,
    premiumFrequency,
    premiumAmountForApi,
    policyTermForPremium,
    markUserEditedQuote,
    isSecondaryAnnuitantComplete,
    setShowSecondaryAnnuitantModal,
    tourActive,
  ],
  );

  const handleSecondaryAnnuitantChange = useCallback((field, value) => {
    setSecondaryAnnuitantForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** When opening the joint modal, seed fields from Redux so returning users see saved secondary data. */
  useEffect(() => {
    if (!showSecondaryAnnuitantModal) return;
    const c = customizePlanData || {};
    setSecondaryAnnuitantForm({
      secondaryAnnuitantName: c.secondaryAnnuitantName,
      secondaryAnnuitantDob: c.secondaryAnnuitantDob,
      secondaryAnnuitantGender: c.secondaryAnnuitantGender,
      secondaryAnnuitantRelation: c.secondaryAnnuitantRelation,
    });
  }, [showSecondaryAnnuitantModal]);

  // Keep secondary display row in sync after drop-off restore (without reopening modal).
  useEffect(() => {
    setSecondaryAnnuitantForm((prev) => {
      const next = {
        secondaryAnnuitantName: customizePlanData?.secondaryAnnuitantName ?? "",
        secondaryAnnuitantDob: customizePlanData?.secondaryAnnuitantDob ?? "",
        secondaryAnnuitantGender:
          customizePlanData?.secondaryAnnuitantGender ?? "",
        secondaryAnnuitantRelation:
          customizePlanData?.secondaryAnnuitantRelation ?? "",
      };
      if (
        prev.secondaryAnnuitantName === next.secondaryAnnuitantName &&
        prev.secondaryAnnuitantDob === next.secondaryAnnuitantDob &&
        prev.secondaryAnnuitantGender === next.secondaryAnnuitantGender &&
        prev.secondaryAnnuitantRelation === next.secondaryAnnuitantRelation
      ) {
        return prev;
      }
      return next;
    });
    setHasSecondaryAnnuitant(
      Boolean(
        customizePlanData?.secondaryAnnuitantDob ||
        customizePlanData?.secondaryAnnuitantGender ||
        customizePlanData?.secondaryAnnuitantRelation ||
        customizePlanData?.secondaryAnnuitantName,
      ),
    );
  }, [
    customizePlanData?.secondaryAnnuitantName,
    customizePlanData?.secondaryAnnuitantDob,
    customizePlanData?.secondaryAnnuitantGender,
    customizePlanData?.secondaryAnnuitantRelation,
  ]);

  const handleSecondaryAnnuitantSave = useCallback(async () => {
    const hasAllRequiredSecondaryFields = Boolean(
      String(secondaryAnnuitantForm?.secondaryAnnuitantName ?? "").trim() &&
      String(secondaryAnnuitantForm?.secondaryAnnuitantDob ?? "").trim() &&
      String(secondaryAnnuitantForm?.secondaryAnnuitantGender ?? "").trim() &&
      String(secondaryAnnuitantForm?.secondaryAnnuitantRelation ?? "").trim(),
    );
    if (!hasAllRequiredSecondaryFields) return;
    const relation = String(
      secondaryAnnuitantForm?.secondaryAnnuitantRelation ?? "",
    ).trim();
    const relationBizCode = resolveSecondaryAnnuitantRelationBizCode(
      relationshipRows,
      relation,
    );
    const merged = {
      ...(customizePlanData || {}),
      ...secondaryAnnuitantForm,
      secondaryAnnuitantRelation: relation,
      secondaryAnnuitantDetailsSaved: true,
    };
    if (relationBizCode) {
      merged.secondaryAnnuitantRelationBizCode = relationBizCode;
    } else if (Array.isArray(relationshipRows) && relationshipRows.length) {
      delete merged.secondaryAnnuitantRelationBizCode;
    }

    dispatch(journeyActions.setCustomizePlanData(merged));
    setShowSecondaryAnnuitantModal(false);
    setHasSecondaryAnnuitant(true);
    lastPlanPrefetchKeyRef.current = null;
    setSelectedPlan(PLAN_NAMES_NISCHIT_PENSION.PLAN_B);
    dispatch(
      journeyActions.setCustomizePlanData({
        ...merged,
        selectedPlan: PLAN_NAMES_NISCHIT_PENSION.PLAN_B,
      }),
    );
    onPlanChange?.(
      PLAN_NAMES_NISCHIT_PENSION.PLAN_B,
      premiumFrequency,
      premiumAmountForApi,
      null,
      null,
      policyTermForPremium,
    );
    if (!applicationNumber) return;
    try {
      const payload = {
        applicationNumber,
        currentStage: "Stage 3.0 Quote",
        productLob: JOURNEY_TYPES.NISCHIT_PENSION,
        ...pickSecondaryAnnuitantFields(merged),
      };
      // Backend ownership check requires primary/proposer DOB proof (body or session).
      // Partial secondary-annuitant saves must include it; UAT often has no sticky session DOB.
      addOwnershipDobProof(
        payload,
        {
          ...(leadResult?.leadInput || {}),
          ...(leadFormData || {}),
        },
        applicationNumber,
      );
      await submitFormData(payload);
    } catch (err) {
      logError("[Quote] secondary annuitant submit failed", err);
    }
  }, [
    customizePlanData,
    secondaryAnnuitantForm,
    dispatch,
    applicationNumber,
    premiumFrequency,
    premiumAmountForApi,
    policyTermForPremium,
    onPlanChange,
    leadFormData,
    leadResult,
    relationshipRows,
  ]);

  const handleSubmitQuote = useCallback(async () => {
    const validationError = validateQuoteSubmit();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      dispatch(journeyActions.setAllowQuoteResubmit(true));
      const leadDob =
        leadFormData?.proposerDob ||
        leadFormData?.dateOfBirth ||
        leadFormData?.lifeAssuredDob ||
        leadResult?.leadInput?.proposerDob ||
        leadResult?.leadInput?.dateOfBirth ||
        leadResult?.leadInput?.lifeAssuredDob ||
        null;
      const redirectedToResumeStage = await syncAndRedirectIfJourneyAdvanced(
        applicationNumber,
        leadDob,
      );
      if (redirectedToResumeStage) return;

      const quoteData = buildQuotePayload();
      // Resume / typed-pincode paths can leave state blank while pincode is present.
      // Resolve from pincode master so BI @CUSCOM / @LI_STATE are never empty for residents.
      let stateOverride = getLeadStateForBi(
        editDetailsFormData,
        leadFormData,
        leadInput,
      );
      if (!stateOverride) {
        const pin = String(
          editDetailsFormData?.residencyPinCode ||
            editDetailsFormData?.pinCode ||
            editDetailsFormData?.pincode ||
            leadFormData?.residencyPinCode ||
            leadFormData?.pinCode ||
            leadInput?.pinCode ||
            leadInput?.pincode ||
            "",
        ).replace(/\D/g, "");
        if (pin.length === 6) {
          try {
            const record = await getPincodeMasterRecord(pin);
            stateOverride =
              record?.state ||
              record?.stateName ||
              record?.stateUT ||
              record?.stateUt ||
              "";
            if (stateOverride) {
              dispatch(
                journeyActions.setLeadFormData({
                  ...(leadFormData || {}),
                  communicationState: stateOverride,
                  residencyState: stateOverride,
                  branchState: stateOverride,
                }),
              );
            }
          } catch (err) {
            logError("[Quote] Resolve state from pincode failed", err);
          }
        }
      }
      const biPayload = buildBISummaryPayload({ stateOverride });
      const result = await submitQuote(
        { ...quoteData, biPayload },
        applicationNumber,
        caseId,
      );
      const biResult = result?.biResult || {};
      const biStatus = String(biResult?.Status || "")
        .trim()
        .toLowerCase();
      const isBiFailed =
      (result?.biGenerated === true && biStatus === "fail") ||
      (result?.biGenerated === false);
      if (isBiFailed) {
        const validationErrors = Array.isArray(biResult?.InputValidationStatus)
          ? biResult.InputValidationStatus.flatMap((entry) =>
              Array.isArray(entry?.ErrorMessage)
                ? entry.ErrorMessage.map((e) => e?.value).filter(Boolean)
                : [],
            )
          : [];
        const resolvedBiErrorMessage =
          validationErrors[0] ||
          biResult?.Message ||
          "BI generation failed. Please review inputs and try again.";
        // Backend still advances to Stage 4.0 QuoteSummary on BI fail; allow the
        // next Pay click to call submitQuote instead of redirecting to /summary.
        dispatch(journeyActions.setAllowQuoteResubmit(true));
        setSubmitError(resolvedBiErrorMessage);
        setBiFailureMessage(resolvedBiErrorMessage);
        setShowBiFailureModal(true);
        return;
      }

      if (onComplete) {
        const pdfUrl =
          biResult?.PDFURL ||
          biResult?.pdfUrl ||
          biResult?.pdfURL ||
          result?.PDFURL ||
          result?.pdfUrl ||
          result?.pdfURL ||
          result?.biPdfUrl ||
          null;
        onComplete({
          result,
          quoteData: {
            ...quoteData,
            biPayload,
            totalPayableAmount,
            premiumFrequency,
            pdfUrl,
          },
        });
      } else {
        setShowSuccessModal(true);
      }
    } catch (err) {
      logError("[Quote] Quote submission failed", err);
      // Stage may already be QuoteSummary after a partial submit; keep Pay able to retry.
      dispatch(journeyActions.setAllowQuoteResubmit(true));
      setSubmitError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to submit quote. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    validateQuoteSubmit,
    buildQuotePayload,
    buildBISummaryPayload,
    editDetailsFormData,
    leadFormData,
    leadInput,
    selectedPlan,
    applicationNumber,
    caseId,
    onComplete,
    totalPayableAmount,
    premiumFrequency,
    leadFormData,
    leadResult,
    setSubmitError,
    syncAndRedirectIfJourneyAdvanced,
    dispatch,
  ]);

  const _handleEditDetailsSave = useCallback(
    async (updatedLeadData = {}) => {
      const editedOnly = stripNestedLeadPayloadKeys(updatedLeadData || {});
      const mergedLeadData = normalizeEditedLeadData({
        ...(leadFormData || {}),
        ...editedOnly,
      });
      // If proposer fields were not explicitly edited, keep proposer in sync
      // with edited life-assured fields so payload never carries stale proposer data.
      if (
        !Object.prototype.hasOwnProperty.call(editedOnly, "proposerName") &&
        (Object.prototype.hasOwnProperty.call(editedOnly, "fullName") ||
          Object.prototype.hasOwnProperty.call(editedOnly, "lifeAssuredName"))
      ) {
        mergedLeadData.proposerName =
          editedOnly.lifeAssuredName ?? editedOnly.fullName ?? mergedLeadData.proposerName;
      }
      if (
        !Object.prototype.hasOwnProperty.call(editedOnly, "proposerDob") &&
        (Object.prototype.hasOwnProperty.call(editedOnly, "dateOfBirth") ||
          Object.prototype.hasOwnProperty.call(editedOnly, "lifeAssuredDob") ||
          Object.prototype.hasOwnProperty.call(editedOnly, "dob"))
      ) {
        mergedLeadData.proposerDob =
          editedOnly.lifeAssuredDob ??
          editedOnly.dateOfBirth ??
          editedOnly.dob ??
          mergedLeadData.proposerDob;
      }
      if (
        !Object.prototype.hasOwnProperty.call(editedOnly, "proposerGender") &&
        (Object.prototype.hasOwnProperty.call(editedOnly, "gender") ||
          Object.prototype.hasOwnProperty.call(editedOnly, "lifeAssuredGender"))
      ) {
        mergedLeadData.proposerGender =
          editedOnly.lifeAssuredGender ??
          editedOnly.gender ??
          mergedLeadData.proposerGender;
      }
      const effectiveAppNumber = applicationNumber || "";
      const stageUpdates = splitLeadEditsByStage(editedOnly);
      const stageKeys = Object.keys(stageUpdates);
      let resp = null;
      try {
        if (effectiveAppNumber && stageKeys.length > 0) {
          for (const stageKey of stageKeys) {
            const payload = {
              applicationNumber: effectiveAppNumber,
              caseId: caseId || "",
              currentStage: stageKey,
              productLob: JOURNEY_TYPES.NISCHIT_PENSION,
              ...stageUpdates[stageKey],
            };
            if (stageKey === "lead_basic") {
              addDobUpdateMetaIfRequired(payload, mergedLeadData, effectiveAppNumber);
            }
            resp = await submitFormData(payload, "lead");
          }
        }
        const persistedDob = resolveLeadDob(mergedLeadData);
        if (persistedDob) {
          syncOwnershipDobInSession(persistedDob, effectiveAppNumber);
        }
        dispatch(journeyActions.setLeadFormData(mergedLeadData));
        dispatch(
          journeyActions.setLeadResult({
            ...(leadResult || {}),
            leadInput: {
              ...(leadResult?.leadInput || {}),
              ...mergedLeadData,
            },
          }),
        );
        if (quoteResultFromStore && typeof quoteResultFromStore === "object") {
          dispatch(
            journeyActions.setQuoteResult({
              ...quoteResultFromStore,
              leadInput: {
                ...(quoteResultFromStore?.leadInput || {}),
                ...mergedLeadData,
              },
            }),
          );
        }
        const updatedJourneyData = applyLeadEditsToJourneyData(
          journeyData,
          mergedLeadData,
        );
        if (updatedJourneyData && typeof updatedJourneyData === "object") {
          dispatch(journeyActions.setJourneyData(updatedJourneyData));
        }
        if (resp && typeof resp === "object") {
          dispatch(
            journeyActions.setLeadResult({
              ...(leadResult || {}),
              ...resp,
              leadInput: {
                ...(leadResult?.leadInput || {}),
                ...(resp?.leadInput || {}),
                ...mergedLeadData,
              },
            }),
          );
        }
        // Edit-step flow must also refresh per-option bulk map (card amount/rate),
        // not only the selected premium result.
        setPlanPremiumByOption({});
        setEditBulkRefreshTick((prev) => prev + 1);
        markUserEditedQuote();
        if (onQuoteDimensionsChange) {
          const planForRecalc =
            selectedPlan || PLAN_NAMES_NISCHIT_PENSION.PLAN_A;
          const coverForRecalc = premiumAmountForApi ?? null;
          const policyTermForRecalc = policyTermForPremium;
          if (coverForRecalc != null && policyTermForRecalc != null) {
          await onQuoteDimensionsChange(
            planForRecalc,
            coverForRecalc,
            policyTermForRecalc,
            premiumFrequency,
            null,
            null,
            mergedLeadData,
          );
          }
        }
        return true;
      } catch (err) {
        logError("[Quote] Edit details submit failed", err);
        setSubmitError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to save details. Please try again.",
        );
        return false;
      }
    },
    [
      leadFormData,
      applicationNumber,
      caseId,
      dispatch,
      leadResult,
      quoteResultFromStore,
      journeyData,
      markUserEditedQuote,
      onQuoteDimensionsChange,
      selectedPlan,
      premiumAmountForApi,
      policyTermForPremium,
      premiumFrequency,
      setSubmitError,
    ],
  );

  const plans = [
    { label: "Single Life Annuity", value: PLAN_NAMES_NISCHIT_PENSION.PLAN_A },
    { label: "Joint Life Annuity", value: PLAN_NAMES_NISCHIT_PENSION.PLAN_B },
  ];

  const activeProductCode = useMemo(
    () => resolveNischitProductCode(leadFormData || {}),
    [leadFormData],
  );
  const nischitProductDisplayName = useMemo(() => {
    const name = getNischitProductConfig(activeProductCode)?.name;
    return name && String(name).trim()
      ? String(name).trim()
      : "Nishchit Pension";
  }, [activeProductCode]);
  const visiblePlanOptions = useMemo(
    () =>
      getNischitVisiblePlanOptions(
        activeProductCode,
        selectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B,
      ),
    [activeProductCode, selectedPlan],
  );
  const visiblePlanOptionIdsKey = useMemo(
    () => visiblePlanOptions.map((o) => o.id).join(","),
    [visiblePlanOptions],
  );
  const selectedNishchitPlans = useMemo(() => {
    const LIFE_EXPECTANCY_YEARS = 85;
    const parseNumericField = (raw) => {
      if (raw == null || raw === "") return null;
      if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
      const cleaned = String(raw).replace(/,/g, "").trim();
      if (cleaned === "") return null;
      const n = Number(cleaned);
      return Number.isFinite(n) ? n : null;
    };
    const rowFreq = (row) => {
      if (!row || typeof row !== "object") return null;
      const addl = row.additionalOutputFields || {};
      const raw = row.annuityPerFrequency ?? addl.annuityPerFrequency;
      return parseNumericField(raw);
    };
    const rowRate = (row) => {
      if (!row || typeof row !== "object") return null;
      const addl = row.additionalOutputFields || {};
      const raw = row.annuityRate ?? addl.annuityRate;
      return parseNumericField(raw);
    };
    const proposerAge = Number.isFinite(Number(age)) ? Number(age) : null;
    const remainingYears =
      proposerAge != null
        ? Math.max(LIFE_EXPECTANCY_YEARS - proposerAge, 0)
        : null;
    const paymentsPerYear = annuityFrequencyToPaymentsPerYear(
      customizePlanData?.annuityFrequency,
    );
    return visiblePlanOptions.map((opt) => {
      const prem = planPremiumByOption?.[opt.id];
      const freq = rowFreq(prem);
      const rate = rowRate(prem);
      const totalPension =
        freq != null && remainingYears != null
          ? freq * remainingYears * paymentsPerYear
          : null;
      return {
        id: opt.id,
        title: opt.label,
        // Only show bulk per-option values here — never fall back to the selected plan's
        // `premiumResult` or every card duplicates the selected row's annuity.
        annuity:
          freq != null
            ? `${formatCurrency(freq)} ${annuityDisplaySuffix}`
            : "—",
        rate: rate != null ? `${rate}` : "—",
        pension: totalPension != null ? formatCurrency(totalPension) : "—",
        lifeExpectancy: `${LIFE_EXPECTANCY_YEARS}yrs`,
        taxSaving: "—",
      };
    });
  }, [
    visiblePlanOptions,
    planPremiumByOption,
    annuityDisplaySuffix,
    age,
    customizePlanData?.annuityFrequency,
  ]);

  useEffect(() => {
    if (!selectedNishchitPlan) return;
     if (!selectedPlan) return; 
    const nextPlanOption = selectedNishchitPlan.id;
    const prem = planPremiumByOption?.[nextPlanOption];
    if (prem && typeof prem === "object") {
      dispatch(journeyActions.setPremiumResult(prem));
    }
    const productCoverageCodes = resolveNischitProductAndCoverageCodes({
      productCode: resolveNischitProductCode(leadFormData || {}),
      planOption: nextPlanOption,
      selectedPlan: selectedNishchitPlan?.title || selectedNishchitPlan?.label,
    });
    const hasSameValues =
      customizePlanData?.selectedNishchitPlanId === nextPlanOption &&
      customizePlanData?.planOption === nextPlanOption &&
      customizePlanData?.ProductCode === productCoverageCodes.ProductCode &&
      customizePlanData?.CoverageType === productCoverageCodes.CoverageType;
    if (hasSameValues) return;

    dispatch(
      journeyActions.setCustomizePlanData({
        ...(customizePlanData || {}),
        selectedNishchitPlanId: nextPlanOption,
        planOption: nextPlanOption,
        ...productCoverageCodes,
      }),
    );
  }, [
    selectedNishchitPlan,
    selectedPlan,
    customizePlanData,
    dispatch,
    planPremiumByOption,
    leadFormData,
  ]);

  useEffect(() => {
  if (selectedNishchitPlan) return;

  const savedPlanId = customizePlanData?.selectedNishchitPlanId;

  if (!savedPlanId) return;

  const savedPlan = selectedNishchitPlans.find(
    (plan) => plan.id === savedPlanId
  );

  if (savedPlan) {
    setSelectedNishchitPlan(savedPlan);
  }
}, [
  selectedNishchitPlan,
  customizePlanData?.selectedNishchitPlanId,
  selectedNishchitPlans,
]);

  useEffect(() => {
    selectedPlanOptionIdRef.current =
      selectedNishchitPlan?.id ??
      customizePlanData?.selectedNishchitPlanId ??
      null;
  }, [selectedNishchitPlan, customizePlanData?.selectedNishchitPlanId]);

  useEffect(() => {
    const planList = visiblePlanOptions;
    if (!planList.length) return;
    if (
      !selectedNishchitPlan ||
      !planList.some((p) => p.id === selectedNishchitPlan.id)
    ) {
      setSelectedNishchitPlan(null);
    }
  }, [visiblePlanOptions, selectedNishchitPlan]);

  /** Plan-option grid prefetch only; primary premium is updated via lead/recalc/dimension handlers — avoid duplicating `calculateNishchitBulk` with a second `onQuoteDimensionsChange` pass. */
  useEffect(() => {
    hasMountedCustomiseSyncRef.current = true;
  }, []);


  /** Shared prefetch-dedup key: same shape used by the automatic (non-guided)
   * debounce effect below and by the guided tour's explicit Calculate click,
   * so both paths agree on whether a given plan/customize combination has
   * already been fetched. */
  const buildPlanPrefetchKey = useCallback(
    () =>
      JSON.stringify({
        selectedPlan,
        optionIds: visiblePlanOptionIdsKey,
        coverAmount: premiumAmountForApi,
        policyTerm: policyTermForPremium,
        premiumFrequency,
        applicationNumber,
        editBulkRefreshTick,
        customize: customizePremiumBulkDriver,
        jointSecondary:
          selectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B
            ? {
                dob: customizePlanData?.secondaryAnnuitantDob ?? null,
                gender: customizePlanData?.secondaryAnnuitantGender ?? null,
                relation: customizePlanData?.secondaryAnnuitantRelation ?? null,
                saved:
                  customizePlanData?.secondaryAnnuitantDetailsSaved === true,
              }
            : null,
      }),
    [
      selectedPlan,
      visiblePlanOptionIdsKey,
      premiumAmountForApi,
      policyTermForPremium,
      premiumFrequency,
      applicationNumber,
      editBulkRefreshTick,
      customizePremiumBulkDriver,
      customizePlanData?.secondaryAnnuitantDob,
      customizePlanData?.secondaryAnnuitantGender,
      customizePlanData?.secondaryAnnuitantRelation,
      customizePlanData?.secondaryAnnuitantDetailsSaved,
    ],
  );

  /** The actual `calculateNishchitBulk` call + result handling, shared by the
   * automatic (non-guided) debounce effect and the guided tour's explicit
   * Calculate click, so both trigger paths behave identically. */
  const runNischitBulkPrefetch = useCallback(
    async (prefetchKey, isCancelled = () => false) => {
      setPlanPremiumLoading(true);
      try {
        const planOptionIds = visiblePlanOptions.map((opt) => opt.id);
        const nextMap = await calculateNischitPremiumsBulkRef.current({
          plan: selectedPlan,
          coverAmount: premiumAmountForApi,
          policyTerm: policyTermForPremium,
          premiumFrequency,
          planOptionIds,
          applicationNumber,
        });
        if (isCancelled()) return;
        // Only surface the failure modal when the plan option the user actually has
        // selected failed to calculate. Other rows in the same bulk response (e.g. a
        // joint-life option still awaiting secondary annuitant details) legitimately
        // return calculationSuccessful:false and must not block an otherwise-successful
        // quote for the selected plan.
        const selectedOptionId = selectedPlanOptionIdRef.current;
        const selectedRow = selectedOptionId ? nextMap?.[selectedOptionId] : null;
        const selectedRowFailed =
          selectedRow &&
          typeof selectedRow === "object" &&
          selectedRow.calculationSuccessful === false;
        if (selectedRowFailed) {
          const failureMessage =
            typeof selectedRow?.errorMessage === "string" &&
            selectedRow.errorMessage.trim()
              ? selectedRow.errorMessage
              : TEXT_CONTENT.CALC_FAILURE_MSG;
          setPlanCalculationFailureMessage(failureMessage);
          setShowPlanCalculationFailureModal(true);
        } else {
          setShowPlanCalculationFailureModal(false);
        }
        lastPlanPrefetchKeyRef.current = prefetchKey;
        setPlanPremiumByOption(
          nextMap && typeof nextMap === "object" ? nextMap : {},
        );
      } catch (err) {
        logError("[Quote] Nishchit bulk plan premium prefetch failed", err);
        if (!isCancelled()) setPlanPremiumByOption({});
      } finally {
        if (!isCancelled()) setPlanPremiumLoading(false);
      }
    },
    [
      visiblePlanOptions,
      selectedPlan,
      premiumAmountForApi,
      policyTermForPremium,
      premiumFrequency,
      applicationNumber,
    ],
  );

  // Skipped entirely while the guided tour is running — the tour's own
  // "Calculate" button on the Select Plan step drives this explicitly (see
  // handleTourCalculatePlanOptions) instead of it firing the instant a plan
  // radio is picked, before the user has actually asked to calculate.
  // Also skipped while Customize your Plan has a validation error, so closing
  // the tour on an invalid form doesn't immediately fire this with the
  // stale (last valid) customize values — it runs once the user corrects the
  // form and re-submits it.
  useEffect(() => {
    if (tourActive) return undefined;
    if (hasCustomizeFormError) return undefined;
    let cancelled = false;
    let debounceTimer = null;

   if (
  !selectedPlan ||
  !visiblePlanOptions.length ||
  !calculateNischitPremiumsBulkRef.current
) {
  return undefined;
}
    if (!leadResult) return undefined;
    if (!hasMountedCustomiseSyncRef.current) return undefined;

    const prefetchKey = buildPlanPrefetchKey();

    if (prefetchKey === lastPlanPrefetchKeyRef.current) {
      return undefined;
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      if (cancelled) return;
      if (prefetchKey === lastPlanPrefetchKeyRef.current) return;
      runNischitBulkPrefetch(prefetchKey, () => cancelled);
    }, PLAN_OPTIONS_PREFETCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    tourActive,
    hasCustomizeFormError,
    selectedPlan,
    visiblePlanOptions,
    leadResult,
    buildPlanPrefetchKey,
    runNischitBulkPrefetch,
  ]);

  /** Guided tour's Select Plan step "Calculate" button: fires the same
   * primary-premium recalc (handlePlanSelect's onPlanChange, deferred while
   * the tour is running) and the same plan-options bulk call the non-guided
   * flow fires automatically — but only on this explicit click, so both
   * only run once both steps' data (customize fields + plan) are actually
   * in place, instead of the instant the plan radio is picked. */
  const handleTourCalculatePlanOptions = useCallback(() => {
    if (!selectedPlan || !visiblePlanOptions.length) return;
    onPlanChange?.(
      selectedPlan,
      premiumFrequency,
      premiumAmountForApi,
      null,
      null,
      policyTermForPremium,
    );
    const prefetchKey = buildPlanPrefetchKey();
    if (prefetchKey === lastPlanPrefetchKeyRef.current) return;
    runNischitBulkPrefetch(prefetchKey);
  }, [
    selectedPlan,
    visiblePlanOptions,
    onPlanChange,
    premiumFrequency,
    premiumAmountForApi,
    policyTermForPremium,
    buildPlanPrefetchKey,
    runNischitBulkPrefetch,
  ]);

  /** Guided tour's Customize your Plan step "Next" button: the form's own
   * Calculate button is hidden for the duration of the tour (see
   * `hideCalculateButton` below), so clicking Next triggers that same
   * (hidden) button instead — same click/submit, so the same validation +
   * premium API path runs either way — then advances to Select Plan. */
  const handleTourCustomizeNext = useCallback((finish, advance) => {
    document.querySelector('[data-tour="calculateButton"]')?.click();
    advance();
  }, []);

  useEffect(() => {
  const savedId = customizePlanData?.selectedNishchitPlanId;

  if (!savedId) {
    setSelectedNishchitPlan(null);
    return;
  }

  const savedPlan = visiblePlanOptions.find(
    (plan) => plan.id === savedId
  );

  if (savedPlan) {
    setSelectedNishchitPlan(savedPlan);
  }
}, [
  customizePlanData?.selectedNishchitPlanId,
  visiblePlanOptions,
]);

  const isPayButtonDisabled = submitting || !applicationNumber || !caseId || hasCustomizeFormError || !selectedNishchitPlan ||  isFormDirtyAfterCalculate;
  const shouldShowSecondaryAnnuitantDetails =
    selectedPlan === PLAN_NAMES_NISCHIT_PENSION.PLAN_B &&
    isSecondaryAnnuitantComplete;

  /** Initial / resume quote: block full page only until primary premium is ready. */
  // const isInitialPremiumFlowBlocking =
  //   !hasUserEditedQuoteRef.current && shouldAutoCalculateOnInitialQuoteLoad;

  const isInitialPremiumFlowBlocking =
  Boolean(selectedPlan) &&
  !hasUserEditedQuoteRef.current &&
  shouldAutoCalculateOnInitialQuoteLoad;

  const shouldShowQuoteLoader =
    !leadResult ||
    (isInitialPremiumFlowBlocking &&
      !premiumError &&
      (!displayPremiumResult || premiumLoading));

  /** After primary premium is visible, suppress the overlay during background prefetch. */
  const suppressPremiumOverlayForInitialGridPrefetchOnly =
    isInitialPremiumFlowBlocking &&
    displayPremiumResult &&
    !premiumLoading &&
    !planPremiumLoading;

  /** Any in-flight premium work (after first paint): single overlay, no stacking with options-only blur. */
  const isPremiumCalculationBusy = premiumLoading || planPremiumLoading;

  /**
   * Guided tour sequence: Customize your Plan (Premium Amount + PPT +
   * Frequency) → Select Plan (Single/Joint Life). The Customize step's
   * action button stays hidden until its fields are filled AND the form has
   * no visible validation error (e.g. Premium Amount below the allowed
   * minimum) — so a guided user can't advance past an error, they see it
   * inline (the form's own `.error-text`) and must fix it first; clicking
   * it triggers the (hidden, see `hideCalculateButton` on CustomizePlan
   * below) real Calculate button so the same validation/premium API path
   * runs as outside the tour. The Select Plan step's button — labeled
   * "Calculate" here since it fires `calculateNishchitBulk` — stays hidden
   * until a plan is actually selected, and only fires that bulk call on
   * click; the automatic (non-guided) debounce effect is suppressed for the
   * duration of the tour (see `tourActive` guard above) so the call only
   * happens once, with both steps' data already in place, instead of the
   * instant the plan radio is picked.
   */
  const quoteTourSteps = useMemo(
    () => [
      {
        target: ".premium-summary-section",
        title: "Customize your Plan",
        description:
          "Enter your Premium Amount, choose a Premium Paying Term, and select a Premium Paying Frequency.",
        isComplete: (root) => {
          const amount = getFieldValue(root, '[data-tour="premiumAmount"]');
          const ppt = getFieldValue(root, '[data-tour="premiumPayingTerm"]');
          const freq = getFieldValue(root, '[data-tour="premiumFrequency"]');
          const annuitantFreq = getFieldValue(root, '[data-tour="annuityFrequency"]');
          const hasValues = !!(
            String(amount || "").trim() &&
            String(ppt || "").trim() &&
            String(freq || "").trim() &&
            String(annuitantFreq || "").trim()
          );
          const hasVisibleError = !!root.querySelector(".error-text");
          return hasValues && !hasVisibleError;
        },
        hideActionUntilComplete: true,
        calloutPosition: "right",
        onAction: handleTourCustomizeNext,
      },
      {
        target: '[data-tour="planSelection"]',
        title: "Select Plan",
        description:
          "Choose Single Life Annuity or Joint Life Annuity based on your requirement.",
        calloutPosition: "right",
        isComplete: () => Boolean(selectedPlan),
        hideActionUntilComplete: true,
        actionLabel: "Calculate",
        onAction: (finish) => {
          // Customize your Plan still has a validation error (e.g. Premium
          // Amount below the minimum, surfaced by the Next click on the
          // previous step) — don't fire any premium API with stale/invalid
          // values. Close the tour instead so the form's own Calculate button
          // comes back, and the user can correct the data and calculate from
          // there (the auto prefetch below stays blocked until the error is
          // cleared by that re-submit).
          if (hasCustomizeFormError) {
            finish();
            document
              .querySelector(".premium-summary-section")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
          handleTourCalculatePlanOptions();
          finish();
        },
      },
    ],
    [
      selectedPlan,
      hasCustomizeFormError,
      handleTourCustomizeNext,
      handleTourCalculatePlanOptions,
    ],
  );

  if (shouldShowQuoteLoader) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader />
      </div>
    );
  }

  return (
    <div className="insurance-journey-quote">
      <header className="quote-header">
        <div className="quote-header-wraper">
          <div className="header-left">
            <h6 className="header-title m-0">{TEXT_CONTENT.PERSONALIZE_TEXT}</h6>
          </div>
          <button
            className="help-button btn secondary-button regular"
            onClick={() => setShowHelpModal(true)}
          >
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6772 2.87806C15.3151 1.50799 13.5769 0.573101 11.683 0.191867C9.789 -0.189367 7.82449 0.000214264 6.03838 0.736587C4.25227 1.47296 2.72496 2.72298 1.64998 4.32823C0.575006 5.93349 0.000755732 7.82174 0 9.75369V15.0037C0 15.6004 0.237053 16.1727 0.65901 16.5947C1.08097 17.0166 1.65326 17.2537 2.25 17.2537H3.75C4.34674 17.2537 4.91903 17.0166 5.34099 16.5947C5.76295 16.1727 6 15.6004 6 15.0037V11.2537C6 10.657 5.76295 10.0847 5.34099 9.6627C4.91903 9.24074 4.34674 9.00369 3.75 9.00369H1.53375C1.67839 7.43988 2.26578 5.94969 3.22709 4.70779C4.18839 3.46588 5.48377 2.52372 6.96141 1.99172C8.43904 1.45972 10.0377 1.35993 11.57 1.70404C13.1024 2.04815 14.5048 2.82191 15.6131 3.93463C16.9677 5.29616 17.8009 7.09031 17.9672 9.00369H15.75C15.1533 9.00369 14.581 9.24074 14.159 9.6627C13.7371 10.0847 13.5 10.657 13.5 11.2537V15.0037C13.5 15.6004 13.7371 16.1727 14.159 16.5947C14.581 17.0166 15.1533 17.2537 15.75 17.2537H18C18 17.8504 17.7629 18.4227 17.341 18.8447C16.919 19.2666 16.3467 19.5037 15.75 19.5037H10.5C10.3011 19.5037 10.1103 19.5827 9.96967 19.7234C9.82902 19.864 9.75 20.0548 9.75 20.2537C9.75 20.4526 9.82902 20.6434 9.96967 20.784C10.1103 20.9247 10.3011 21.0037 10.5 21.0037H15.75C16.7446 21.0037 17.6984 20.6086 18.4017 19.9053C19.1049 19.2021 19.5 18.2483 19.5 17.2537V9.75369C19.5049 8.47829 19.2581 7.21446 18.7737 6.03462C18.2893 4.85477 17.5768 3.7821 16.6772 2.87806ZM3.75 10.5037C3.94891 10.5037 4.13968 10.5827 4.28033 10.7234C4.42098 10.864 4.5 11.0548 4.5 11.2537V15.0037C4.5 15.2026 4.42098 15.3934 4.28033 15.534C4.13968 15.6747 3.94891 15.7537 3.75 15.7537H2.25C2.05109 15.7537 1.86032 15.6747 1.71967 15.534C1.57902 15.3934 1.5 15.2026 1.5 15.0037V10.5037H3.75ZM15.75 15.7537C15.5511 15.7537 15.3603 15.6747 15.2197 15.534C15.079 15.3934 15 15.2026 15 15.0037V11.2537C15 11.0548 15.079 10.864 15.2197 10.7234C15.3603 10.5827 15.5511 10.5037 15.75 10.5037H18V15.7537H15.75Z"
                fill="#97262A"
              />
            </svg>
            Need Help?
          </button>
        </div>
      </header>

      <section className="plan-banner">
        <div className="plan-wraper">
          <div className="plan-banner-left">
            <h6 className="plan-banner-label m-0">Protection Plan</h6>
            <h2 className="plan-banner-title bold">
              {nischitProductDisplayName}
            </h2>
          </div>
          <div className="plan-banner-right">
            <div className="user-info">
              <span>{lifeAssuredName}</span> <span>|</span>{" "}
              <span>
                {age} years ({formattedDob})
              </span>
            </div>

            <button
              className="edit-details-button "
              onClick={() => setShowEditDetailsModal(true)}
            >
              <span>Show Details</span>
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.64178 5.16656L7.30845 2.83323M0.600098 11.8749L2.57431 11.6555C2.81551 11.6287 2.93612 11.6153 3.04884 11.5788C3.14885 11.5465 3.24403 11.5007 3.33178 11.4429C3.4307 11.3776 3.5165 11.2918 3.6881 11.1202L11.3918 3.41656C12.0361 2.77223 12.0361 1.72756 11.3918 1.08322C10.7474 0.438893 9.70278 0.438892 9.05845 1.08322L1.35477 8.78688C1.18317 8.95849 1.09736 9.04429 1.03214 9.14321C0.974267 9.23096 0.928523 9.32614 0.896147 9.42615C0.859655 9.53887 0.846255 9.65948 0.819455 9.90068L0.600098 11.8749Z"
                  stroke="white"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section className="plan-and-summary-section">
        <div className="plan-and-summary-wraper">
          <aside className="aside-section">
            <CustomizePlan
              hideCalculateButton={tourActive}
              onValidationChange={setHasCustomizeFormError}
              setIsFormDirtyAfterCalculate={setIsFormDirtyAfterCalculate}
            />
            <QuickPeep />
          </aside>
          <div className="plan-selection-section">
            <div className="plan-options" data-tour="planSelection">
              {plans.map((plan) => (
                <div
                  key={plan.value}
                  className={`plan-option ${selectedPlan === plan.value ? "selected" : ""}`}
                  onClick={() => {
                    handlePlanSelect(plan.value);
                  }}
                >
                  <div className="radio-button">
                    <input
                      type="radio"
                      name="plan"
                      checked={selectedPlan === plan.value}
                      onChange={() => handlePlanSelect(plan.value)}
                      readOnly
                    />
                  </div>
                  <div className="plan-option-content">
                    <h6 className="plan-option-title m-0">
                      <span className="font-medium">{plan.label}</span>
                    </h6>
                  </div>
                </div>
              ))}
            </div>

            <div className="plans-detail-wraper">
              {shouldShowSecondaryAnnuitantDetails && (
                <div className="secondary-plan-section">
                  <div className="secondary-annuitant-details">
                    <span className="text-caption medium">
                      Joint Life Details:{" "}
                      {secondaryAnnuitantForm.secondaryAnnuitantName} |{" "}
                      {calculateAge(
                        secondaryAnnuitantForm.secondaryAnnuitantDob,
                      )}{" "}
                      years |{" "}
                      {secondaryAnnuitantForm.secondaryAnnuitantGender} |{" "}
                      {secondaryAnnuitantForm.secondaryAnnuitantRelation}
                    </span>
                  </div>
                  <button
                    className="secondary-annuitant-edit btn tertiary-button regular"
                    type="button"
                    onClick={() => setShowSecondaryAnnuitantModal(true)}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 13 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.2069 3.0861L9.41438 0.292974C9.32152 0.200091 9.21127 0.126411 9.08993 0.0761423C8.96859 0.0258733 8.83853 0 8.70719 0C8.57585 0 8.4458 0.0258733 8.32446 0.0761423C8.20312 0.126411 8.09287 0.200091 8 0.292974L0.29313 8.00047C0.199867 8.093 0.125926 8.20313 0.0756045 8.32448C0.025283 8.44584 -0.000414649 8.57598 5.05934e-06 8.70735V11.5005C5.05934e-06 11.7657 0.105362 12.02 0.292898 12.2076C0.480435 12.3951 0.734788 12.5005 1 12.5005H3.79313C3.9245 12.5009 4.05464 12.4752 4.17599 12.4249C4.29735 12.3746 4.40748 12.3006 4.5 12.2073L12.2069 4.50047C12.2998 4.40761 12.3734 4.29736 12.4237 4.17602C12.474 4.05468 12.4999 3.92463 12.4999 3.79329C12.4999 3.66195 12.474 3.53189 12.4237 3.41055C12.3734 3.28921 12.2998 3.17896 12.2069 3.0861ZM3.79313 11.5005H1V8.70735L6.5 3.20735L9.29313 6.00047L3.79313 11.5005ZM10 5.29297L7.20688 2.50047L8.70688 1.00047L11.5 3.79297L10 5.29297Z"
                        fill="#97262A"
                      />
                    </svg>
                    <span>Edit details</span>
                  </button>
                </div>
              )}
              <div
                className="cover-amount-section card-plan-list"
                data-tour="subPlanSelection"
              >
                <div className="plan-list-row">
                  {selectedNishchitPlans.map((plan) => (
                    <label
                      key={plan.id}
                      className={`plan-options-item ${
                        selectedNishchitPlan?.id === plan.id ? "selected" : ""
                      }`}
                    >
                      {/* Left Section */}
                      <div className="input-text">
                        <input
                          type="radio"
                          name="plan-selection"
                          checked={selectedNishchitPlan?.id === plan.id}
                          onChange={() => setSelectedNishchitPlan(plan)}
                        />

                        <div className="plan-left">
                          <span className="tax-saving">
                            Tax saving of {plan.taxSaving}
                          </span>

                          <h6 className="section-title large-body bold m-0">
                            {plan.title}
                          </h6>
                          <div className="plan-left-content">
                            <p className="plan-desc small-body light">
                              Life Expectancy{" "}
                              <span className="small-body semibold">
                                {plan.lifeExpectancy}
                              </span>{" "}
                              | Total Pension{" "}
                              <span className="small-body semibold">
                                {plan.pension}
                              </span>
                            </p>
                            <span className="badge">No medical required</span>
                          </div>
                        </div>
                      </div>

                      {/* Right Section */}
                      <div className="recommended-text">
                        <div className="recommended-text-content">
                          <p className="small-body regular">Annuity Amount</p>
                          <p className="rate text-caption regular">
                            {plan.rate}% Annuity rate
                          </p>
                        </div>

                        <h5 className="amount bold">{plan.annuity}</h5>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="quote-submit-section">
        
        <div className="quote-submit-strip">
          <div className="quote-submit-rigth w-100">
            <button
              type="button"
              className="btn-submit-quote btn primary-button regular"
              onClick={handleSubmitQuote}
              disabled={isPayButtonDisabled}
            >
              {submitting
                ? "Submitting Quote..."
                : `Pay ${formatCurrency(footerPayAmount)}`}
            </button>
          </div>
        </div>
      </section>

      {/* Premium loading overlay: skip while initial grid prefetch only (primary premium already shown) to avoid full-page → overlay flash. */}
      {(isPremiumCalculationBusy || submitting) &&
        !suppressPremiumOverlayForInitialGridPrefetchOnly && (
          <div
            className="quote-premium-loader-overlay"
            aria-live="polite"
            aria-busy="true"
          >
            <BrandLoader />
          </div>
        )}

      {/* Modals */}
      <NeedhelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title="Reach us through your preferred mode"
        type="success"
      />
      <JointLifeAnnuitantModal
        isOpen={showSecondaryAnnuitantModal}
        onClose={() => setShowSecondaryAnnuitantModal(false)}
        form={secondaryAnnuitantForm}
        onChange={handleSecondaryAnnuitantChange}
        onSave={handleSecondaryAnnuitantSave}
        secondaryAnnuitantMinAge={activeProductCode === "22074" ? 40 : 30}
        relationOptions={secondaryRelationOptions}
      />
      <Modal
        isOpen={showBiFailureModal}
        onClose={() => setShowBiFailureModal(false)}
        title="Unable to Generate Quote"
        type="success"
        btnTitle="Okay"
      >
        <p>{biFailureMessage ||  TEXT_CONTENT.BI_GEN_FAILED}</p>
      </Modal>

      <Modal
        isOpen={showPlanCalculationFailureModal}
        onClose={() => setShowPlanCalculationFailureModal(false)}
        title="Unable to Calculate Plan Options"
        type="success"
        btnTitle="Okay"
      >
        <p>
          {planCalculationFailureMessage ||
           TEXT_CONTENT. PLAN_CALC_FAILURE}
        </p>
      </Modal>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Quote Submitted Successfully!"
        type="success"
        btnTitle="Done"
      >
        <p>
          {TEXT_CONTENT.QUOTE_SUBMIT}
        </p>
      </Modal>

      {/* Edit Details Modal - shows all lead form fields with prefilled data */}
      <EditStepModal
        isOpen={showEditDetailsModal}
        onClose={() => setShowEditDetailsModal(false)}
        title="Show Details"
        // onSave={handleEditDetailsSave}
        className="quote-edit"
        formData={editDetailsFormData}
        disableAllFields={true}
      />

      {/* storageKey: only plays once per browser tab session (sessionStorage) —
          replays on a fresh tab/session, but not on every remount of this page
          (e.g. navigating back from Summary) once the user has seen it. */}
      <GuidedTour
        steps={quoteTourSteps}
        storageKey={quoteTourSessionKey}
        active={
          !isPremiumCalculationBusy &&
          !submitting &&
          !showSecondaryAnnuitantModal &&
          !showEditDetailsModal &&
          !showBiFailureModal &&
          !showPlanCalculationFailureModal &&
          !showSuccessModal
        }
        onActiveChange={setTourActive}
      />
    </div>
  );
};

export default NischitPensionQuote;
